import React, { useState, useEffect, useCallback, useRef } from "react";
import "./../../../../assets/css/purchase/form.css";

import InitialDataFormLayout from "../components/InitialDataFormLayout";
import {
  labelStyle,
  inputStyle,
  selectStyle,
  sectionHeaderStyle,
  sectionIconStyle,
  sectionTitleStyle,
} from "../components/formStyles";

import {
  deleteData,
  getAlert,
  insertData,
  updateData,
} from "../../../../utils/SamuiUtils";

const DEFAULT_FORM = {
  ServiceAddr_Type_Id: 0,
  ServiceAddr_Type_Name: "",
  ServiceAddr_Type_Status: "Y",
};

const ServiceAddrTypeForm = ({
  callInitialize,
  mode,
  name,
  formData,
  setFormData,
  selectedData,
}) => {
  const [initialFormData, setInitialFormData] = useState(DEFAULT_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (mode === "I") {
      setFormData(DEFAULT_FORM);
      setInitialFormData(DEFAULT_FORM);
    } else if (mode === "U" && selectedData) {
      const editData = {
        ServiceAddr_Type_Id: selectedData.ServiceAddr_Type_Id,
        ServiceAddr_Type_Name: selectedData.ServiceAddr_Type_Name || "",
        ServiceAddr_Type_Status: selectedData.ServiceAddr_Type_Status || "Y",
      };
      setFormData(editData);
      setInitialFormData(editData);
    }
  }, [mode, selectedData, setFormData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInsert = useCallback(async () => {
    try {
      const { ServiceAddr_Type_Id, ...dataToInsert } = formData;
      const fields = Object.keys(dataToInsert).join(", ");
      const values = Object.values(dataToInsert);

      await insertData("Tb_Set_ServiceAddrType", fields, values);
      await callInitialize();
      setFormData(DEFAULT_FORM);
      setInitialFormData(DEFAULT_FORM);
    } catch (error) {
      console.error("Error in handleInsert:", error);
      getAlert("FAILED", "ไม่สามารถบันทึกข้อมูลได้");
    }
  }, [formData, callInitialize, setFormData]);

  const handleUpdate = useCallback(async () => {
    try {
      const where = { ServiceAddr_Type_Id: formData.ServiceAddr_Type_Id };
      const { ServiceAddr_Type_Id, ...values } = formData;
      const res = await updateData("Tb_Set_ServiceAddrType", values, where);
      if (res) {
        await callInitialize();
      }
    } catch (error) {
      getAlert(
        "FAILED",
        "ไม่สามารถอัปเดตข้อมูลได้ เนื่องจากมีข้อมูลนี้อยู่ในระบบแล้ว"
      );
    }
  }, [formData, callInitialize]);

  const handleDelete = async () => {
    try {
      getAlert("C_DIALOG", "ยืนยันการลบข้อมูล", async (isConfirm) => {
        if (isConfirm && selectedData?.ServiceAddr_Type_Id !== undefined) {
          await deleteData(
            "Tb_Set_ServiceAddrType",
            `WHERE ServiceAddr_Type_Id = ${selectedData.ServiceAddr_Type_Id}`
          );
          await callInitialize();
        }
      });
    } catch (error) {
      getAlert("FAILED", "ไม่สามารถลบข้อมูลได้ กรุณาลองใหม่อีกครั้ง");
    }
  };

  const handleSubmit = useCallback(async () => {
    if (isSavingRef.current) return;
    if (!formData.ServiceAddr_Type_Name) {
      getAlert("WARNING", "โปรดกรอกชื่อประเภทอาคาร");
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      if (mode === "I") {
        await handleInsert();
      } else if (mode === "U") {
        await handleUpdate();
      }
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [formData.ServiceAddr_Type_Name, mode, handleInsert, handleUpdate]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.tagName === "SELECT"
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        getAlert("C_DIALOG", "ต้องการยกเลิกการทำงานหรือไม่?", (isConfirm) => {
          if (isConfirm) {
            callInitialize();
          }
        });
      }

      if (event.key === "F10") {
        event.preventDefault();
        if (!isSavingRef.current) handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSubmit, callInitialize]);

  const handleReset = () => {
    setFormData(initialFormData);
  };

  const handleBack = () => {
    callInitialize();
  };

  return (
    <InitialDataFormLayout
      name={name}
      entityPath="/serviceaddrtype"
      mode={mode}
      onSubmit={handleSubmit}
      isSubmittingDisabled={isSaving}
      onBack={handleBack}
      onReset={handleReset}
      onDelete={mode === "U" ? handleDelete : undefined}
      callInitialize={callInitialize}
    >
      <div style={sectionHeaderStyle}>
        <i className="fas fa-building" style={sectionIconStyle}></i>
        <h5 style={sectionTitleStyle}>ประเภทอาคาร</h5>
      </div>
      <div className="row g-4">
        <div className="col-md-6">
          <label style={labelStyle}>ประเภทอาคาร</label>
          <input
            type="text"
            className="form-control"
            name="ServiceAddr_Type_Name"
            value={formData.ServiceAddr_Type_Name || ""}
            onChange={handleChange}
            style={inputStyle}
          />
        </div>
        <div className="col-md-6">
          <label style={labelStyle}>สถานะ</label>
          <select
            className="form-select"
            name="ServiceAddr_Type_Status"
            value={formData.ServiceAddr_Type_Status || "Y"}
            onChange={handleChange}
            style={selectStyle}
          >
            <option value="Y">ใช้งาน</option>
            <option value="N">ไม่ใช้งาน</option>
          </select>
        </div>
      </div>
    </InitialDataFormLayout>
  );
};

export default ServiceAddrTypeForm;
